/*
  Product-card interactions: colour swatches and the touch image strip.

  Everything is delegated from the document so it covers every card on the page
  and keeps working after the collection filters re-render the grid over AJAX —
  listeners bound per card would be lost on the first filter change.
*/
(() => {
  if (window.__dlySwatches) return;
  window.__dlySwatches = true;

  const basePath = (url) => (url || '').split('?')[0].split('/').pop();

  /* ---------------------------------------------------------------- swatches */

  function swap(swatch) {
    const card = swatch.closest('.card-wrapper, .card');
    if (!card) return;

    const { img: src, srcset, alt } = swatch.dataset;

    // Touch layout shows a scrollable strip rather than one image, so the
    // swatch scrolls to that colour's photo instead of rewriting a src.
    const gallery = card.querySelector('[data-dly-card-gallery]');
    if (gallery && gallery.offsetParent !== null) {
      const target = [...gallery.querySelectorAll('.dly-card-gallery__img')]
        .find((im) => basePath(im.getAttribute('src')) === basePath(src));
      // A jump, not a smooth scroll: with scroll-snap a smooth programmatic scroll
      // can be dropped entirely (measured: the strip stayed on the first photo),
      // and the circle then looked dead. Desktop swaps instantly too.
      if (target) {
        gallery.scrollTo({ left: target.offsetLeft });
        // The dots normally follow scroll/IntersectionObserver callbacks, which a
        // throttled page may not deliver; the index is known here, so set it.
        setDot(gallery, [...gallery.querySelectorAll('.dly-card-gallery__img')].indexOf(target));
      }
    }

    // Desktop: Dawn's stacked pair -- the picture, then the hover frame.
    const media = card.querySelector('.card__media .media');
    const img = media && media.querySelector(':scope > img:not(.dly-card-gallery__img)');
    if (img) {
      if (srcset) img.setAttribute('srcset', srcset);
      if (src) img.setAttribute('src', src);
      if (alt) img.setAttribute('alt', alt);

      // The hover frame follows the colour too. The pointer is usually on the
      // card when a swatch is clicked, so the hover frame is the picture on
      // screen: rewriting only the first image changed nothing visible until
      // the pointer left the card. The colour's own second photo goes in; when
      // it has none, the frame is dropped and `dly-hover-off` keeps the new
      // picture showing under the pointer.
      const { hoverImg, hoverSrcset, hoverAlt } = swatch.dataset;
      let hover = img.nextElementSibling && img.nextElementSibling.matches('img:not(.dly-card-gallery__img)')
        ? img.nextElementSibling
        : null;
      if (hoverImg) {
        if (!hover) {
          // Must sit directly after the picture: Dawn's hover keys on `img + img`.
          hover = img.cloneNode(false);
          hover.removeAttribute('fetchpriority');
          img.after(hover);
        }
        hover.setAttribute('loading', 'eager');
        if (hoverSrcset) hover.setAttribute('srcset', hoverSrcset);
        hover.setAttribute('src', hoverImg);
        hover.setAttribute('alt', hoverAlt || '');
        media.classList.remove('dly-hover-off');
      } else {
        if (hover) hover.remove();
        media.classList.add('dly-hover-off');
      }
    }

    // Opening the card goes to the colour that was picked.
    if (swatch.dataset.url) {
      card.querySelectorAll('a.full-unstyled-link[href*="/products/"]').forEach((a) => {
        a.setAttribute('href', swatch.dataset.url);
      });
    }

    const row = swatch.closest('[data-dly-swatches]');
    if (row) {
      row.querySelectorAll('.dly-swatch').forEach((s) => {
        s.classList.toggle('is-active', s === swatch);
      });
    }
  }

  // Fetch a colour's two photos as soon as the pointer reaches its circle, so the
  // card can change the moment it is clicked instead of a second later. Same
  // srcset and sizes as the card's own image, so the browser picks the same file.
  document.addEventListener('mouseover', (event) => {
    const swatch = event.target.closest && event.target.closest('.dly-swatch');
    if (!swatch || swatch.dataset.dlyPreloaded) return;
    swatch.dataset.dlyPreloaded = '1';
    const cardImg = swatch.closest('.card-wrapper, .card')?.querySelector('.card__media .media > img');
    const sizes = cardImg ? cardImg.getAttribute('sizes') : '';
    [[swatch.dataset.img, swatch.dataset.srcset], [swatch.dataset.hoverImg, swatch.dataset.hoverSrcset]].forEach(([src, srcset]) => {
      if (!src) return;
      const im = new Image();
      if (sizes) im.sizes = sizes;
      if (srcset) im.srcset = srcset;
      im.src = src;
    });
  });

  document.addEventListener('click', (event) => {
    const swatch = event.target.closest('.dly-swatch');
    if (!swatch) return;
    // The whole card is a link, so the click has to be stopped from following it.
    event.preventDefault();
    event.stopPropagation();
    swap(swatch);
  });

  /* --------------------------------------------------- tap through the strip */

  // The strip sits above the card's full-tile link so it can receive the swipe,
  // which means the link no longer gets the tap. A native scroll never fires a
  // click, so a click here is a genuine tap and should open the product.
  document.addEventListener('click', (event) => {
    const gallery = event.target.closest('[data-dly-card-gallery]');
    if (!gallery) return;
    const card = gallery.closest('.card-wrapper, .card');
    const link = card && card.querySelector('a[href*="/products/"]');
    if (link) window.location.href = link.href;
  });

  /* ------------------------------------------- full-screen product viewer */

  // The lightbox is one photo per screen. Arrows step it, a swipe does the same
  // on touch, and the counter says where you are.
  const modalTrack = (el) =>
    el.closest('.product-media-modal')?.querySelector('.product-media-modal__content');

  function updateModalChrome(track, index) {
    const modal = track.closest('.product-media-modal');
    if (!modal) return;
    const total = track.children.length;
    const i = index ?? Math.round(track.scrollLeft / (track.clientWidth || 1));
    const counter = modal.querySelector('[data-dly-modal-counter]');
    if (counter) counter.textContent = total ? `${i + 1} / ${total}` : '';
    const prev = modal.querySelector('[data-dly-modal-prev]');
    const next = modal.querySelector('[data-dly-modal-next]');
    if (prev) prev.toggleAttribute('disabled', i <= 0);
    if (next) next.toggleAttribute('disabled', i >= total - 1);
  }

  // Position is read with an IntersectionObserver rather than a timer after the
  // scroll: the arrows animate, and guessing how long that takes gives a counter
  // that lags or reports the wrong photo.
  function bindModal(track) {
    if (!track || track.dataset.dlyBound) return;
    track.dataset.dlyBound = '1';

    // Dawn's ModalDialog closes a `media-modal` on ANY mouse pointerup inside it
    // (global.js: it only exempts deferred-media and product-model). So a click
    // on an arrow dismissed the lightbox instead of paging it. The event has to
    // be stopped at the button, before it bubbles to the modal -- a delegated
    // listener on document is too late, since the modal is the nearer ancestor.
    const modal = track.closest('.product-media-modal');
    if (modal) {
      modal.querySelectorAll('.dly-modal-nav').forEach((btn) => {
        ['pointerup', 'pointerdown', 'mouseup', 'mousedown'].forEach((type) => {
          btn.addEventListener(type, (event) => event.stopPropagation());
        });
      });
    }
    const slides = [...track.children];
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const i = slides.indexOf(entry.target);
          track.dataset.dlyIndex = String(i);   // keeps swipes and arrows agreed
          updateModalChrome(track, i);
        });
      },
      { root: track, threshold: 0.6 }
    );
    slides.forEach((s) => io.observe(s));
    updateModalChrome(track);
  }

  // Where the viewer is *meant* to be, which is not the same as where it is.
  // The scroll animates, so reading scrollLeft straight after a click returns the
  // old position: press next twice quickly and both presses compute the same
  // starting slide, so the second does nothing and the arrows appear stuck. The
  // intended index is therefore held on the element and the observer syncs it
  // back whenever a slide actually settles (which is what a swipe does).
  function currentIndex(track) {
    const stored = parseInt(track.dataset.dlyIndex ?? '', 10);
    if (!Number.isNaN(stored)) return stored;
    return Math.round(track.scrollLeft / (track.clientWidth || 1));
  }

  function step(track, dir) {
    bindModal(track);
    const width = track.clientWidth || 1;
    const total = track.children.length;
    const target = Math.min(Math.max(currentIndex(track) + dir, 0), total - 1);
    track.dataset.dlyIndex = String(target);
    // Absolute, not scrollBy: with `scroll-snap-type: mandatory` a relative
    // scroll re-snaps mid-animation and the error compounds.
    track.scrollTo({ left: target * width, behavior: 'smooth' });
    updateModalChrome(track, target);
  }

  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-dly-modal-prev], [data-dly-modal-next]');
    if (btn) {
      event.preventDefault();
      const track = modalTrack(btn);
      if (track) step(track, btn.hasAttribute('data-dly-modal-next') ? 1 : -1);
      return;
    }
    // Opening the lightbox: Dawn scrolls the clicked photo into view, so the
    // chrome is refreshed just after the modal is on screen.
    if (event.target.closest('.product__modal-opener')) {
      setTimeout(() => {
        const track = document.querySelector('.product-media-modal__content');
        if (!track) return;
        // Dawn scrolls the clicked photo into view, so the stored index is
        // reset from the real position before the arrows are used.
        delete track.dataset.dlyIndex;
        bindModal(track);
        updateModalChrome(track);
      }, 250);
    }
  });

  // Belt and braces: bind on load too, so the arrows work even if the modal is
  // opened by something other than the image (keyboard, a deep link, Dawn's own
  // scripts) and the click above never ran.
  const bindModalTracks = () =>
    document.querySelectorAll('.product-media-modal__content').forEach(bindModal);
  bindModalTracks();
  document.addEventListener('DOMContentLoaded', bindModalTracks);
  document.addEventListener('shopify:section:load', bindModalTracks);

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    const modal = document.querySelector('.product-media-modal[open]')
      || [...document.querySelectorAll('.product-media-modal')].find((m) => m.getBoundingClientRect().height > 0);
    const track = modal && modal.querySelector('.product-media-modal__content');
    if (!track) return;
    event.preventDefault();
    step(track, event.key === 'ArrowRight' ? 1 : -1);
  });

  /* ------------------------------------------------------------ strip dots */

  function setDot(gallery, index) {
    const card = gallery.closest('.card-wrapper, .card');
    const dots = card && card.querySelector('[data-dly-card-dots]');
    if (!dots) return;
    dots.querySelectorAll('.dly-card-gallery__dot').forEach((d, i) => {
      d.classList.toggle('is-active', i === index);
    });
  }

  // Driven by an IntersectionObserver rooted on the strip rather than by scroll
  // events: "which photo is showing" is a visibility question, the observer
  // coalesces its own callbacks instead of firing on every pixel of a flick, and
  // it does not depend on scroll events reaching a listener at all.
  function bind(gallery) {
    if (gallery.dataset.dlyBound) return;
    gallery.dataset.dlyBound = '1';
    const imgs = [...gallery.querySelectorAll('.dly-card-gallery__img')];
    if (imgs.length < 2) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setDot(gallery, imgs.indexOf(entry.target));
        });
      },
      { root: gallery, threshold: 0.6 }
    );
    imgs.forEach((im) => io.observe(im));

    // Belt and braces. The observer is the primary reading, but its callbacks run
    // in the rendering lifecycle, which a browser is free to skip -- a background
    // tab, a phone that has throttled the page. A scroll then moves the strip with
    // the dots left behind, and the dots are the only thing telling the shopper
    // there are more photos.
    // Coalesced on a timer rather than requestAnimationFrame on purpose: rAF is
    // part of that same lifecycle, so using it here would inherit exactly the
    // failure this is meant to cover. Timers still fire. The two paths agree on
    // the answer, so whichever runs first wins and the other is a no-op.
    let tick = 0;
    gallery.addEventListener(
      'scroll',
      () => {
        if (tick) return;
        tick = setTimeout(() => {
          tick = 0;
          const w = gallery.clientWidth || 1;
          setDot(gallery, Math.max(0, Math.min(imgs.length - 1, Math.round(gallery.scrollLeft / w))));
        }, 60);
      },
      { passive: true }
    );
  }

  const bindAll = () =>
    document.querySelectorAll('[data-dly-card-gallery]').forEach(bind);

  bindAll();
  document.addEventListener('DOMContentLoaded', bindAll);
  // The collection filters re-render the whole grid over AJAX, so new strips
  // have to be picked up as they appear.
  document.addEventListener('shopify:section:load', bindAll);

  let rescan;
  new MutationObserver(() => {
    clearTimeout(rescan);
    rescan = setTimeout(bindAll, 100);
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
